/**
 * List Builder types: cities and items (activities, meals, hotels)
 * used to preload knowledge about what to do and where to stay when planning itineraries.
 */

export interface Geocode {
  latitude: number;
  longitude: number;
}

export interface Address {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export interface CityHistory {
  summary: string;
}

export interface ListBuilderCity {
  id: string;
  name: string;
  country: string;
  region?: string;
  notes?: string;
  /** History summary for the city; added to trip when city is used on a trip */
  history?: CityHistory;
}

export type TransitType = 'walk' | 'metro' | 'tram' | 'bus' | 'rideshare' | 'bike';
export type PriceCategory = 'free' | '$' | '$$' | '$$$' | '$$$$';

export interface ListBuilderActivity {
  id: string;
  cityId: string;
  title: string;
  description?: string | null;
  operatingHours?: string | null;
  recommendedTimes?: string[];
  bestTimeReason?: string | null;
  area?: string | null;
  url?: string | null;
  suggestedDurationMinutes?: number;
  transit?: TransitType;
  kidFriendly?: boolean;
  accessibilityNotes?: string | null;
  priceCategory?: PriceCategory;
  tags?: string[];
  recommendedIfStayAtLeastDays?: number;
  geocode?: Geocode | null;
  address?: Address | null;
  phoneNumber?: string | null;
}

export type ReservationRequirement = 'required' | 'recommended' | 'not_needed';

export interface ListBuilderMeal {
  id: string;
  cityId: string;
  name: string;
  cuisine?: string | null;
  description?: string | null;
  operatingHours?: string | null;
  priceCategory?: PriceCategory;
  area?: string | null;
  url?: string | null;
  matchScore?: number;
  reasoning?: string | null;
  reservationRequirement?: ReservationRequirement;
  kidFriendly?: boolean;
  tags?: string[];
  geocode?: Geocode | null;
  address?: Address | null;
  phoneNumber?: string | null;
}

export interface ListBuilderHotel {
  id: string;
  cityId: string;
  name: string;
  description?: string | null;
  budgetCategory?: string | null;
  pricePerNight?: number;
  currency?: string | null;
  area?: string | null;
  url?: string | null;
  amenities?: string[];
  rating?: number;
  familyNotes?: string | null;
  accessibilityNotes?: string | null;
  tags?: string[];
  geocode?: Geocode | null;
  address?: Address | null;
  phoneNumber?: string | null;
}

export type ListItemType = 'activity' | 'meal' | 'hotel';

export const createEmptyCity = (): Omit<ListBuilderCity, 'id'> => ({
  name: '',
  country: '',
  region: '',
  notes: '',
  history: { summary: '' },
});

export const createEmptyActivity = (): Omit<ListBuilderActivity, 'id'> => ({
  cityId: '',
  title: '',
  description: null,
  operatingHours: null,
  recommendedTimes: [],
  bestTimeReason: null,
  area: null,
  url: null,
  suggestedDurationMinutes: undefined,
  transit: undefined,
  kidFriendly: undefined,
  accessibilityNotes: null,
  priceCategory: undefined,
  tags: [],
  recommendedIfStayAtLeastDays: undefined,
  geocode: null,
  address: null,
  phoneNumber: null,
});

export const createEmptyMeal = (): Omit<ListBuilderMeal, 'id'> => ({
  cityId: '',
  name: '',
  cuisine: null,
  description: null,
  operatingHours: null,
  priceCategory: undefined,
  area: null,
  url: null,
  matchScore: undefined,
  reasoning: null,
  reservationRequirement: undefined,
  kidFriendly: undefined,
  tags: [],
  geocode: null,
  address: null,
  phoneNumber: null,
});

export const createEmptyHotel = (): Omit<ListBuilderHotel, 'id'> => ({
  cityId: '',
  name: '',
  description: null,
  budgetCategory: null,
  pricePerNight: undefined,
  currency: null,
  area: null,
  url: null,
  amenities: [],
  rating: undefined,
  familyNotes: null,
  accessibilityNotes: null,
  tags: [],
  geocode: null,
  address: null,
  phoneNumber: null,
});
